import React, { useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Linkedin, Twitter, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { TCLIconImage } from './TCLMark';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FooterNewsletter = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/cfq/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, first_name: firstName, source: 'footer' }),
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex items-center gap-3 py-2" data-testid="footer-newsletter-success">
        <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
        <p className="text-sm" style={{ color: 'rgba(250,248,243,0.55)' }}>
          Check your inbox to confirm your subscription.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2" data-testid="footer-newsletter-form">
      <input
        type="text"
        value={firstName}
        onChange={e => setFirstName(e.target.value)}
        placeholder="First name"
      className="w-full sm:w-32 bg-white/5 border border-white/12 px-3.5 py-3.5 text-base text-cream placeholder-white/30 focus:outline-none focus:border-gold/40 transition-colors"
        data-testid="footer-newsletter-firstname"
      />
      <input
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        placeholder="Your email address"
        required
        className="flex-1 bg-white/5 border border-white/12 px-3.5 py-3.5 text-base text-cream placeholder-white/30 focus:outline-none focus:border-gold/40 transition-colors"
        data-testid="footer-newsletter-email"
      />
      <button
        type="submit"
        disabled={!email || status === 'loading'}
        className="inline-flex items-center justify-center gap-1.5 px-5 py-3 text-sm font-medium transition-all disabled:opacity-40 flex-shrink-0"
        style={{ backgroundColor: '#C99316', color: '#071B3A' }}
        data-testid="footer-newsletter-submit"
      >
        {status === 'loading'
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <><span>Subscribe</span><ArrowRight className="w-3.5 h-3.5" /></>
        }
      </button>
    </form>
  );
};

const Footer = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();

  const [showAdminLink, setShowAdminLink] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const holdTimerRef = useRef(null);
  const tapTimerRef = useRef(null);

  const handleCopyrightHoldStart = useCallback(() => {
    holdTimerRef.current = setTimeout(() => {
      setShowAdminLink(true);
      sessionStorage.setItem('adminLinkRevealed', 'true');
    }, 3000);
  }, []);

  const handleCopyrightHoldEnd = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  }, []);

  const handleLogoTap = useCallback(() => {
    setTapCount(prev => {
      const newCount = prev + 1;
      if (tapTimerRef.current) clearTimeout(tapTimerRef.current);
      tapTimerRef.current = setTimeout(() => setTapCount(0), 2000);
      if (newCount >= 7) {
        setShowAdminLink(true);
        sessionStorage.setItem('adminLinkRevealed', 'true');
        return 0;
      }
      return newCount;
    });
  }, []);

  React.useEffect(() => {
    const revealed = sessionStorage.getItem('adminLinkRevealed');
    if (revealed === 'true') setShowAdminLink(true);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Books', path: '/books' },
    { name: 'Speaking', path: '/speaking' },
    { name: 'Articles', path: '/articles' },
    { name: 'Podcast', path: '/podcast' },
    { name: 'Social', path: '/social' },
    { name: 'About', path: '/about' },
    { name: 'Promond Publishing', path: '/promond-publishing' },
    { name: 'Bio Links', path: '/bio-links' },
    { name: 'Subscribe to Mailing List', path: '/subscribe' },
  ];

  const learnLinks = [
    { name: 'Courses', path: '/courses' },
    { name: 'CFQ Assessment', path: '/cfq-assessment' },
    { name: 'Cultural Fluency Matrix™', path: '/cfq-matrix' },
    { name: 'Four Pillars', path: '/pillars' },
    { name: 'Sign In / Register', path: '/signin' },
    { name: 'Facilitator Portal', path: '/facilitate/login' },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'Support', path: '/support' },
  ];

  return (
    <footer className="bg-navy-deep text-cream/60" data-testid="site-footer">
      {/* Gold divider */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 pt-16 pb-8">
        {/* Newsletter strip */}
        <div id="subscribe" className="mb-14 pb-12 border-b border-white/[0.06]">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12">
            <div className="md:w-80 flex-shrink-0">
              <p className="text-[10px] uppercase tracking-[0.28em] mb-1.5" style={{ color: 'rgba(201,147,22,0.75)' }}>Cultural Fluency List</p>
              <h3 className="font-serif text-xl font-light leading-snug mb-1" style={{ color: 'rgba(250,248,243,0.9)' }}>Stay close to the work</h3>
              <p className="text-xs leading-relaxed" style={{ color: 'rgba(250,248,243,0.55)' }}>
                New essays, frameworks and session insights — delivered when they're ready, not on a schedule.
              </p>
            </div>
            <div className="flex-1">
              <FooterNewsletter />
              <p className="text-[10px] mt-2" style={{ color: 'rgba(250,248,243,0.35)' }}>Double opt-in. No spam. Unsubscribe any time.</p>
            </div>
          </div>
        </div>

        {/* 4-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-14">
          {/* Col 1: Brand */}
          <div>
            <button
              onClick={handleLogoTap}
              className="text-left mb-5 block"
              aria-label="Third Culture Leadership"
              data-testid="footer-logo"
            >
              {/* Standalone brand icon — blends cleanly on dark footer */}
              <TCLIconImage size={52} />
            </button>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(250,248,243,0.62)' }}>
              Third Culture Leadership equips leaders to navigate cultural complexity, communicate across difference, and build trusted relationships — without compromising identity, values, or integrity.
            </p>
          </div>

          {/* Col 2: Navigation */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-gold/70 font-sans font-medium mb-5">
              Navigate
            </h4>
            <ul className="space-y-2.5">
              {navLinks.map(link => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className={`text-sm transition-colors duration-200 ${
                      link.name === 'Subscribe to Mailing List'
                        ? 'text-gold/75 hover:text-gold font-medium'
                        : 'text-cream/62 hover:text-gold'
                    }`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Learn */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-gold/70 font-sans font-medium mb-5">
              Learn
            </h4>
            <ul className="space-y-2.5">
              {learnLinks.map(link => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-cream/62 hover:text-gold transition-colors duration-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Contact & Connect */}
          <div>
            <h4 className="text-[11px] uppercase tracking-[0.2em] text-gold/70 font-sans font-medium mb-5">
              Contact
            </h4>
            <ul className="space-y-3 mb-6">
              <li>
                <a href="mailto:contact@thirdcultureleadership.co.uk" className="text-sm text-cream/62 hover:text-gold transition-colors duration-200 flex items-center gap-2">
                  <Mail size={14} className="text-gold/55" />
                  General Enquiries
                </a>
              </li>
              <li>
                <a href="mailto:promise.monday@thirdcultureleadership.co.uk" className="text-sm text-cream/62 hover:text-gold transition-colors duration-200 flex items-center gap-2">
                  <Mail size={14} className="text-gold/55" />
                  Speaking Enquiries
                </a>
              </li>
              <li>
                <a href="mailto:culturalfluency.book@thirdcultureleadership.co.uk" className="text-sm text-cream/62 hover:text-gold transition-colors duration-200 flex items-center gap-2">
                  <Mail size={14} className="text-gold/55" />
                  Book Enquiries
                </a>
              </li>
            </ul>
            <div className="flex gap-3 mb-5">
              <a href="https://www.linkedin.com/company/third-culture-leadership" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-cream/10 flex items-center justify-center hover:border-gold/50 hover:text-gold transition-all duration-200" aria-label="LinkedIn">
                <Linkedin size={15} />
              </a>
              <a href="https://x.com/promisemonday_" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full border border-cream/10 flex items-center justify-center hover:border-gold/50 hover:text-gold transition-all duration-200" aria-label="Twitter">
                <Twitter size={15} />
              </a>
              <a href="mailto:contact@thirdcultureleadership.co.uk" className="w-9 h-9 rounded-full border border-cream/10 flex items-center justify-center hover:border-gold/50 hover:text-gold transition-all duration-200" aria-label="Email">
                <Mail size={15} />
              </a>
            </div>
            <div className="space-y-1.5">
              {legalLinks.map(link => (
                <Link key={link.path} to={link.path} className="block text-xs text-cream/42 hover:text-cream/65 transition-colors">
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-cream/5 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p
            className="text-xs text-cream/38 select-none cursor-default"
            onMouseDown={handleCopyrightHoldStart}
            onMouseUp={handleCopyrightHoldEnd}
            onMouseLeave={handleCopyrightHoldEnd}
            onTouchStart={handleCopyrightHoldStart}
            onTouchEnd={handleCopyrightHoldEnd}
            data-testid="footer-copyright"
          >
            &copy; {new Date().getFullYear()} Third Culture Leadership. All rights reserved.
          </p>
          {showAdminLink && (
            <Link
              to={isAuthenticated && isAuthenticated() && isAdmin && isAdmin() ? '/admin' : '/admin/login'}
              className="text-xs text-cream/15 hover:text-cream/40 transition-colors"
              data-testid="footer-admin-link"
            >
              Admin
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
